/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { GenericTable } from "@ui-components/Table/GenericTable";
import { GenericFilter } from "@ui-components/Filter/GenericFilter";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { ReactComponent as IconRefresh } from "../../../shared/assets/Icons/IconRefreshV2.svg";
import InstanceDetailsModal from "./components/InstanceDetailsModal";
import { useAppSelector } from "@redux/hooks";
import { SkeletonLine, SkeletonTableRows, SkeletonBase } from "@ui-components/Skeleton/GenericSkeleton";


/* BGP, Cloud, Instances, HA interfaces/columns */
import {
	Status,
	statusColumns,
	CloudHealth,
	cloudColumns,
	InstanceData,
	instancesColumns,
	HaData,
	haColumns,
} from "./StatusData";

/* Requests for BGP, Cloud, Instances, HA */
import {
	getNeighborsStatus,
	getCloudHealth,
	getInstancesStatus,
	getHAStatus,
} from "./request/request";
import EmptyItems from "@shared/components/EmptyItems";

/** BGP "raw" interface */
interface RawNeighborItem {
	neighbor: string;
	as: number;
	local_as: number;
	peer_as: number;
	up: string;
	state: string;
	ctxr: string;
	ctxr_description: string;
	in: number;
	out: number;
	description?: string | null;
}

/** Instances "raw" interface */
interface InstanceMetadata {
	instance_id?: string;
	instance_type?: string;
	cloud_hostname?: string;
	local_hostname?: string;
	availability_zone?: string;
	security_groups?: string;
}
interface RawInstanceItem {
	name: string;
	region: string;
	description: string;
	lastupdate: string;
	Suscription: string;
	version: string | null;
	metadata: InstanceMetadata;
}

/** HA "raw" interface */
interface RawHAItem {
	id: number;
	name: string;
	enable: boolean;
	description: string;
	ctxr: Array<{
		subid: string;
		name: string;
		description: string;
		status: string;
		lastupdate: string | null;
	}>;
	suscription: string;
}

export function StatusPage() {
	const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
	const userProfile = useAppSelector((state) => state.userProfile);
	const { tenantId } = useAppSelector(state => state.selection);
	const hasReflector = userProfile.ProductList.some(
		(product) => product.id === 1
	);

	// Single global spinner (kept for backward compatibility but not used in render)
	const [isFetching, setIsFetching] = useState(false);

	// Modal state for instance details
	const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
	const [selectedInstance, setSelectedInstance] = useState<InstanceData | null>(
		null
	);

	// ---- BGP state ----
	const [neighborData, setNeighborData] = useState<Status[]>([]);
	const [bgpSearchTerm, setBgpSearchTerm] = useState("");
	const [bgpSearchColumn, setBgpSearchColumn] = useState("All");
	const [bgpLoaded, setBgpLoaded] = useState(false);

	// ---- Cloud state ----
	const [cloudData, setCloudData] = useState<CloudHealth[]>([]);
	const [cloudSearchTerm, setCloudSearchTerm] = useState("");
	const [cloudSearchColumn, setCloudSearchColumn] = useState("All");
	const [cloudLoaded, setCloudLoaded] = useState(false);

	// ---- Instances state ----
	const [instanceList, setInstanceList] = useState<InstanceData[]>([]);
	const [instanceSearchTerm, setInstanceSearchTerm] = useState("");
	const [instanceSearchColumn, setInstanceSearchColumn] = useState("All");
	const [instancesLoaded, setInstancesLoaded] = useState(false);

	// ---- HA state ----
	const [haList, setHaList] = useState<HaData[]>([]);
	const [haSearchTerm, setHaSearchTerm] = useState("");
	const [haSearchColumn, setHaSearchColumn] = useState("All");
	const [haLoaded, setHaLoaded] = useState(false);

	// Handle viewing instance details
	const handleViewDetails = (instance: InstanceData) => {
		setSelectedInstance(instance);
		setIsDetailsModalOpen(true);
	};

	// ========== Fetch BGP ==========
	const fetchNeighbors = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getNeighborsStatus(token, tenantId);

			const shaped: Status[] = (resp.data as RawNeighborItem[]).map((item) => ({
				ctxr: item.ctxr ?? "",
				ctxr_description: item.ctxr_description ?? "",
				neighbor: item.neighbor ?? "",
				description: item.description ?? "",
				state: item.state ?? "",
				up: item.up ?? "",
				local_as: item.local_as ?? 0,
				peer_as: item.peer_as ?? 0,
				in: item.in || 0,
				out: item.out || 0,
				onViewDetails: handleViewDetails,
			}));
			setNeighborData(shaped);
			setBgpLoaded(true);
		} catch (err) {
			console.error("Error fetching neighbors =>", err);
			// On error, set empty data and mark as loaded to show empty table
			setNeighborData([]);
			setBgpLoaded(true);
		} finally {
			setIsFetching(false);
		}
	};

	// ========== Fetch Cloud ==========
	const fetchCloud = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getCloudHealth(token, tenantId);
			setCloudData(resp.data as CloudHealth[]);
			setCloudLoaded(true);
		} catch (err) {
			console.error("Error fetching Cloud =>", err);
			// On error, set empty data and mark as loaded to show empty table
			setCloudData([]);
			setCloudLoaded(true);
		} finally {
			setIsFetching(false);
		}
	};

	// ========== Fetch Instances ==========
	const fetchInstances = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getInstancesStatus(token, tenantId);

			const shaped: InstanceData[] = (resp.data as RawInstanceItem[]).map(
				(raw) => ({
					name: raw.name ?? "",
					description: raw.description ?? "",
					region: raw.region ?? "",
					status: raw.lastupdate ?? "unknown",
					version: raw.version ?? null,
					metadata: raw.metadata ?? {},
					onViewDetails: handleViewDetails,
				})
			);
			setInstanceList(shaped);
			setInstancesLoaded(true);
		} catch (err) {
			console.error("Error fetching Instances =>", err);
			// On error, set empty data and mark as loaded to show empty table
			setInstanceList([]);
			setInstancesLoaded(true);
		} finally {
			setIsFetching(false);
		}
	};

	// ========== Fetch HA ==========
	const fetchHA = async () => {
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getHAStatus(token, tenantId);

			const shaped: HaData[] = (resp.data as RawHAItem[]).map((item) => ({
				clusterName: item.name ?? "",
				description: item.description ?? "",
				status: item.enable ? "Enabled" : "Disabled",
			}));
			setHaList(shaped);
			setHaLoaded(true);
		} catch (err) {
			console.error("Error fetching HA =>", err);
			// On error, set empty data and mark as loaded to show empty table
			setHaList([]);
			setHaLoaded(true);
		} finally {
			setIsFetching(false);
		}
	};

	// ----- On Mount / if Auth ready -----
	useEffect(() => {
		if (isLoading) return;
		if (!isAuthenticated) return;

		// Reset loaded flags whenever we start
		setBgpLoaded(false);
		setCloudLoaded(false);
		setInstancesLoaded(false);
		setHaLoaded(false);

		fetchNeighbors();
		fetchCloud();
		fetchInstances();
		fetchHA();
	}, [isLoading, isAuthenticated, getAccessTokenSilently, tenantId]);

	// ---- REFRESH Buttons ----
	const handleRefreshBGP = async () => {
		setBgpLoaded(false);
		await fetchNeighbors();
	};
	const handleRefreshCloud = async () => {
		setCloudLoaded(false);
		await fetchCloud();
	};
	const handleRefreshInstances = async () => {
		setInstancesLoaded(false);
		await fetchInstances();
	};
	const handleRefreshHA = async () => {
		setHaLoaded(false);
		await fetchHA();
	};

	// =========== BGP Filter ===========
	const filteredBGPData = neighborData.filter((row) => {
		const term = bgpSearchTerm.toLowerCase();
		if (!term) return true;
		const includes = (val: string) => val.toLowerCase().includes(term);

		switch (bgpSearchColumn) {
			case "CTX-R":
				return includes(row.ctxr) || includes(row.ctxr_description);
			case "Neighbor":
				return includes(row.neighbor) || includes(row.description ?? "");
			case "Status/Time":
				return includes(row.state) || includes(row.up);
			case "AS":
				return (
					includes(row.local_as.toString()) || includes(row.peer_as.toString())
				);
			case "Prefix":
				return includes(row.in.toString()) || includes(row.out.toString());
			case "All":
			default:
				return (
					includes(row.ctxr) ||
					includes(row.ctxr_description) ||
					includes(row.neighbor) ||
					includes(row.description ?? "") ||
					includes(row.state) ||
					includes(row.up) ||
					includes(row.local_as.toString()) ||
					includes(row.peer_as.toString()) ||
					includes(row.in.toString()) ||
					includes(row.out.toString())
				);
		}
	});

	// =========== Cloud Filter ===========
	const filteredCloudData = cloudData.filter((row) => {
		const term = cloudSearchTerm.toLowerCase();
		if (!term) return true;
		const includes = (val: string) => val.toLowerCase().includes(term);

		switch (cloudSearchColumn) {
			case "Cloud":
				return includes(row.cloud);
			case "Region":
				return includes(row.region);
			case "Status":
				return includes(row.status);
			case "All":
			default:
				return (
					includes(row.cloud) || includes(row.region) || includes(row.status)
				);
		}
	});

	// =========== Instances Filter ===========
	const filteredInstanceData = instanceList.filter((row) => {
		const term = instanceSearchTerm.toLowerCase();
		if (!term) return true;
		const includes = (val: string) => val.toLowerCase().includes(term);

		switch (instanceSearchColumn) {
			case "Name":
				return includes(row.name);
			case "Description":
				return includes(row.description);
			case "Region":
				return includes(row.region);
			case "Status":
				return includes(row.status);
			case "Version":
				return includes(row.version ?? "");
			case "All":
			default:
				return (
					includes(row.name) ||
					includes(row.description) ||
					includes(row.region) ||
					includes(row.status) ||
					includes(row.version ?? "")
				);
		}
	});

	// =========== HA Filter ===========
	const filteredHAData = haList.filter((row) => {
		const term = haSearchTerm.toLowerCase();
		if (!term) return true;
		const includes = (val: string) => val.toLowerCase().includes(term);

		switch (haSearchColumn) {
			case "Cluster":
				return includes(row.clusterName);
			case "Description":
				return includes(row.description);
			case "Status":
				return includes(row.status);
			case "All":
			default:
				return (
					includes(row.clusterName) ||
					includes(row.description) ||
					includes(row.status)
				);
		}
	});

	// Skeleton component for card content (filters + table)
	const CardSkeleton: React.FC<{ cols: number }> = ({ cols }) => (
		<div>
			{/* Filter skeleton */}
			<div className="flex items-center gap-2 p-4 justify-end">
				<div className="flex items-center gap-2">
					<SkeletonLine w="120px" h={32} />
					<SkeletonLine w="180px" h={32} />
				</div>
				<SkeletonBase w={10} h={10} roundedClass="rounded" />
			</div>
			{/* Table skeleton */}
			<div className="p-4">
				<SkeletonTableRows rows={5} cols={cols} rowHeight={18} />
			</div>
		</div>
	);

	// If not authenticated or loading, show nothing
	if (isLoading || !isAuthenticated) {
		return null;
	}

	// If user doesn't have Reflector in their product list, show empty state
	if (!hasReflector) {
		return <EmptyItems />;
	}

	// If all data is loaded, render the tables
	return (
		<div className="relative pb-16">
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{/* CLOUD STATUS */}
				<GenericCard
					headerContent={<p className="text-body-large">Cloud Status</p>}
					isCollapsible={false}
					bodyContent={
						cloudLoaded ? (
							<>
								<div>
									<div className="flex items-center gap-2 p-4 justify-end">
										<GenericFilter
											searchColumn={cloudSearchColumn}
											onSearchColumnChange={setCloudSearchColumn}
											searchTerm={cloudSearchTerm}
											onSearchTermChange={setCloudSearchTerm}
											columnOptions={["All", "Cloud", "Region", "Status"]}
										/>
										<GenericButton
											label=""
											variant="secondary"
											onClick={handleRefreshCloud}
											icon={<IconRefresh className="w-5 h-5" />}
										/>
									</div>
								</div>
								<GenericTable
									columns={cloudColumns}
									data={filteredCloudData}
									initialRowsPerPage={5}
									rowsPerPageOptions={[5, 10]}
									width="100%"
									tableClassName="bg-semantic-bg-primary"
								/>
							</>
						) : (
							<CardSkeleton cols={3} />
						)
					}
				/>

				{/* INSTANCES STATUS */}
				<GenericCard
					headerContent={<p className="text-body-large">Instances Status</p>}
					isCollapsible={false}
					bodyContent={
						instancesLoaded ? (
							<>
								<div>
									<div className="flex items-center gap-2 p-4 justify-end">
										<GenericFilter
											searchColumn={instanceSearchColumn}
											onSearchColumnChange={setInstanceSearchColumn}
											searchTerm={instanceSearchTerm}
											onSearchTermChange={setInstanceSearchTerm}
											columnOptions={[
												"All",
												"Name",
												"Description",
												"Region",
												"Status",
												"Version",
											]}
										/>
										<GenericButton
											label=""
											variant="secondary"
											onClick={handleRefreshInstances}
											icon={<IconRefresh className="w-5 h-5" />}
										/>
									</div>
								</div>
								<GenericTable
									columns={instancesColumns}
									data={filteredInstanceData}
									initialRowsPerPage={5}
									rowsPerPageOptions={[5, 10]}
									width="100%"
									tableClassName="bg-semantic-bg-primary"
								/>
							</>
						) : (
							<CardSkeleton cols={5} />
						)
					}
				/>

				{/* HIGH AVAILABILITY STATUS */}
				<GenericCard
					headerContent={
						<p className="text-body-large">High Availability Status</p>
					}
					isCollapsible={false}
					bodyContent={
						haLoaded ? (
							<>
								<div>
									<div className="flex items-center gap-2 p-4 justify-end">
										<GenericFilter
											searchColumn={haSearchColumn}
											onSearchColumnChange={setHaSearchColumn}
											searchTerm={haSearchTerm}
											onSearchTermChange={setHaSearchTerm}
											columnOptions={["All", "Cluster", "Description", "Status"]}
										/>
										<GenericButton
											label=""
											variant="secondary"
											onClick={handleRefreshHA}
											icon={<IconRefresh className="w-5 h-5" />}
										/>
									</div>
								</div>
								<GenericTable
									columns={haColumns}
									data={filteredHAData}
									initialRowsPerPage={5}
									rowsPerPageOptions={[5, 10]}
									width="100%"
									tableClassName="bg-semantic-bg-primary"
								/>
							</>
						) : (
							<CardSkeleton cols={3} />
						)
					}
				/>

				{/* BGP Sessions */}
				<GenericCard
					headerContent={<p className="text-body-large">BGP Sessions status</p>}
					isCollapsible={false}
					bodyContent={
						bgpLoaded ? (
							<>
								<div>
									<div className="flex items-center gap-2 p-4 justify-end">
										<GenericFilter
											searchColumn={bgpSearchColumn}
											onSearchColumnChange={setBgpSearchColumn}
											searchTerm={bgpSearchTerm}
											onSearchTermChange={setBgpSearchTerm}
											columnOptions={[
												"All",
												"CTX-R",
												"Neighbor",
												"Status/Time",
												"AS",
											]}
										/>
										<GenericButton
											label=""
											variant="secondary"
											onClick={handleRefreshBGP}
											icon={<IconRefresh className="w-5 h-5" />}
										/>
									</div>
								</div>
								<GenericTable
									columns={statusColumns}
									data={filteredBGPData}
									initialRowsPerPage={5}
									rowsPerPageOptions={[5, 10]}
									width="100%"
									tableClassName="bg-semantic-bg-primary"
								/>
							</>
						) : (
							<CardSkeleton cols={5} />
						)
					}
				/>
			</div>

			<InstanceDetailsModal
				isOpen={isDetailsModalOpen}
				onClose={() => setIsDetailsModalOpen(false)}
				instanceData={selectedInstance}
			/>
		</div>
	);
}

export default StatusPage;
